import React, { useEffect } from 'react';
import { Row, Col, Typography, Statistic, Card, Select, Space } from 'antd';
import { createUseStyles } from 'react-jss';
import { palette } from '@shipium-corp/shipium-ui-theme';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { useStoreActions } from '~/store';
import type { SimulationDetail, SimulationRawResultsV2FilterOptions } from '~/store/simulation/types';
import { SimulationRawResultsTableV2, parseStatus, PROCESSING_POLL_MS } from './SimulationRawResultsTableV2';
import type { StatusFilter } from './SimulationRawResultsTableV2';
import { useSimulationRawResultsFilterOptions } from '~/hooks/simulation/useSimulationRawResultsV2';
import { useCarriersFiltered } from '~/hooks/queries/useCarriersFiltered';
import { useTenantsQuery } from '~/hooks/queries/useTenantsQuery';
import { timeInTransitDataTypeMap } from './utils';

const { Text, Title } = Typography;

const useStyles = createUseStyles({
  filterBar: {
    marginBottom: '16px',
  },
  filterLabel: {
    marginRight: '5px',
  },
  statCard: {
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
    '&:hover': {
      borderColor: palette.Grey.light,
    },
  },
});

// Data-driven filters from /raw-results/filter-options; each maps to the same-named list param.
const EXTRA_FILTERS: {
  key: keyof SimulationRawResultsV2FilterOptions;
  param: 'carrier' | 'tntType' | 'dayOfWeek' | 'tenantId' | 'originSlug';
  label: string;
  allLabel: string;
  width: number;
}[] = [
  { key: 'originSlugs', param: 'originSlug', label: 'Origin', allLabel: 'All Origins', width: 150 },
  { key: 'tenantIds', param: 'tenantId', label: 'Tenant', allLabel: 'All Tenants', width: 180 },
  { key: 'carriers', param: 'carrier', label: 'Carrier', allLabel: 'All Carriers', width: 300 },
  {
    key: 'tntTypes',
    param: 'tntType',
    label: 'Time in Transit Source',
    allLabel: 'All Time in Transit Sources',
    width: 220,
  },
  { key: 'daysOfWeek', param: 'dayOfWeek', label: 'Day of Week', allLabel: 'All Days', width: 150 },
];

// statuses where the sim is still running and new results/counts keep arriving
const IN_FLIGHT_STATUSES = ['processing', 'finalizing'];

export interface SimulationShipmentsTabV2Props {
  simulation: SimulationDetail;
}

export const SimulationShipmentsTabV2 = ({ simulation }: SimulationShipmentsTabV2Props) => {
  const classes = useStyles();
  const simulationId = simulation.simulationId;
  const batches = simulation.simulationShipmentBatches || [];
  const isProcessing = IN_FLIGHT_STATUSES.includes(simulation.simulationStatus);
  const fetchSimulationDetail = useStoreActions((actions) => actions.simulation.fetchSimulationDetail);

  // In-flight sim: refresh the detail (stat cards + status) on an interval; the raw-results table polls
  // its own list in parallel. Polling stops once the refreshed status flips off 'processing'.
  useEffect(() => {
    if (!isProcessing) return undefined;
    const id = setInterval(() => fetchSimulationDetail(simulationId), PROCESSING_POLL_MS);
    return () => clearInterval(id);
  }, [isProcessing, simulationId, fetchSimulationDetail]);

  // Total assumes batch totals (totalRows etc.) equal the raw-results `options.count` from the v2 table below.
  const totalRowsSum = batches.reduce((s, b) => s + (b.totalRows || 0), 0);
  const skippedRowsSum = batches.reduce((s, b) => s + (b.skippedRows || 0), 0);
  // "Simulated" card value is Σsuccess − Σskipped (v1 convention), but its click filters status=success.
  // These only agree if batch successRows includes skipped rows — don't "fix" this into a real mismatch
  // without confirming that assumption against the service. Clamp to 0 so a violated assumption shows a
  // safe value rather than a negative count in the card.
  const simulatedRowsSum = Math.max(0, batches.reduce((s, b) => s + (b.successRows || 0), 0) - skippedRowsSum);
  const errorRowsSum = batches.reduce((s, b) => s + (b.errorRows || 0), 0);

  const [query, setQuery] = useSearchParams();
  const status = parseStatus(query.get('status'));

  // filters aren't available until the sim finalizes; skip the fetch while in-flight and let RQ fetch
  // once the status flips off in-flight (the detail poll drives that transition) — no reload needed
  const { data: filterOptions } = useSimulationRawResultsFilterOptions(simulationId, !isProcessing);
  const carrierMap = useCarriersFiltered();
  const { data: tenantsData } = useTenantsQuery();

  // Facet values come back as ids/codes; map to human-readable names where we have a source.
  const labelFor = (param: string, value: string): string => {
    if (param === 'carrier') return carrierMap[value]?.name || value;
    if (param === 'tntType') return timeInTransitDataTypeMap[value] || value;
    if (param === 'tenantId') return tenantsData?.tenantsRecord[value]?.name || value;
    return value;
  };

  const setStatus = (next: StatusFilter) => {
    if (next === 'all') query.delete('status');
    else query.set('status', next);
    query.set('page', '1');
    setQuery(query);
  };

  // Filters + status share the URL with the table below, which reads these params for its list query.
  const setFilter = (param: string, value: string) => {
    if (!value || value === 'all') query.delete(param);
    else query.set(param, value);
    query.set('page', '1');
    setQuery(query);
  };

  const statCard = (title: string, value: number, color: string, blurb: string, cardStatus: StatusFilter) => (
    <Col span={6}>
      <Card
        bordered
        className={classes.statCard}
        onClick={() => setStatus(cardStatus)}
        style={{
          borderColor: status === cardStatus ? palette.Teal.light : undefined,
          borderWidth: 1,
          minHeight: 215,
          height: '100%',
          background: status === cardStatus ? '#F1FCFA' : '#fafafa',
        }}
      >
        <Statistic title={<Title level={4}>{title}</Title>} value={value} precision={0} valueStyle={{ color }} />
        <Text type="secondary">{blurb}</Text>
      </Card>
    </Col>
  );

  return (
    <>
      <Row gutter={32} align="stretch">
        {statCard(
          'Total Shipments',
          totalRowsSum,
          palette.Grey.darkest,
          'Total shipments within the simulation date range.',
          'all',
        )}
        {statCard(
          'Simulated Shipments',
          simulatedRowsSum,
          palette.Teal.dark,
          'Shipments executed as part of the simulation. Excludes unmapped shipments and errors.',
          'success',
        )}
        {statCard(
          'Skipped Shipments',
          skippedRowsSum,
          palette.Gold.dark,
          'Shipments with no fulfillment context mapping defined for the simulation.',
          'skipped',
        )}
        {statCard(
          'Errors',
          errorRowsSum,
          palette.Coral.dark,
          'Shipments for which simulation was attempted but failed.',
          'error',
        )}
      </Row>
      <Row gutter={16}>
        <Col span={24}>&nbsp;</Col>
      </Row>
      {filterOptions && EXTRA_FILTERS.some((f) => filterOptions[f.key]?.length) && (
        <div className={classes.filterBar}>
          <Space size="large" wrap>
            {EXTRA_FILTERS.filter((f) => filterOptions[f.key]?.length).map((f) => (
              <span key={f.param}>
                <Title className={classes.filterLabel} level={5}>
                  {f.label}:
                </Title>
                <Select
                  style={{ width: f.width }}
                  value={query.get(f.param) || 'all'}
                  onChange={(value) => setFilter(f.param, value)}
                  options={[
                    { label: f.allLabel, value: 'all' },
                    ...filterOptions[f.key].map((value) => ({ value, label: labelFor(f.param, value) })),
                  ]}
                />
              </span>
            ))}
          </Space>
        </div>
      )}
      <Card bodyStyle={{ padding: 0 }}>
        <SimulationRawResultsTableV2 simulationId={simulationId} isProcessing={isProcessing} />
      </Card>
    </>
  );
};
